import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { 
    User, 
    Mail, 
    Phone, 
    Euro,
    CreditCard,
    CheckCircle2,
    Loader2,
    AlertCircle,
    Car, 
    Hammer, 
    Wallet, 
    Plane, 
    Heart, 
    PartyPopper, 
    GraduationCap, 
    Armchair, 
    Briefcase, 
    Settings, 
    MapPin,
    MoreHorizontal
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const categoryIcons = {
    vehicles: Car,
    debts: CreditCard,
    renovations: Hammer,
    liquidity: Wallet,
    travel: Plane,
    health: Heart,
    celebration: PartyPopper,
    studies: GraduationCap,
    furniture: Armchair,
    business: Briefcase,
    mechanical: Settings,
    land: MapPin,
    others: MoreHorizontal,
};

export const LoanApplicationForm = ({ isOpen, onClose, category }) => {
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        email: '',
        currentDebt: '',
        requestedAmount: '',
        acceptTerms: false
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [error, setError] = useState(null);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleCheckboxChange = (checked) => {
        setFormData(prev => ({ ...prev, acceptTerms: checked }));
    };

    const formatNumber = (value) => {
        const num = value.replace(/\D/g, '');
        return num.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
    };

    const handleAmountChange = (e) => {
        const { name, value } = e.target;
        const formatted = formatNumber(value);
        setFormData(prev => ({ ...prev, [name]: formatted }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.acceptTerms) {
            setError(t('loanForm.acceptTermsError'));
            return;
        }
        
        setIsSubmitting(true);
        setError(null);
        
        try {
            const response = await fetch(`${BACKEND_URL}/api/contact`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: formData.name,
                    email: formData.email,
                    phone: formData.phone,
                    subject: `loan-${category}`,
                    message: `Loan Application for: ${t(`simulator.categories.${category}`)}\n\nCurrent Debt: ${formData.currentDebt}\nRequested Amount: ${formData.requestedAmount}`
                }),
            });
            
            const data = await response.json();
            
            if (!response.ok) {
                throw new Error(data.detail || 'Failed to send application');
            }
            
            setIsSubmitted(true);
        } catch (err) {
            console.error('Error sending loan application:', err);
            setError(err.message || t('loanForm.submitError'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        onClose();
        setTimeout(() => {
            setFormData({
                name: '',
                phone: '',
                email: '',
                currentDebt: '',
                requestedAmount: '',
                acceptTerms: false
            });
            setIsSubmitted(false);
            setError(null);
        }, 300);
    };

    const CategoryIcon = categoryIcons[category] || CreditCard;

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[550px] p-0 overflow-hidden max-h-[90vh] overflow-y-auto">
                {!isSubmitted ? (
                    <>
                        {/* Header with gradient */}
                        <div className="bg-gradient-to-r from-primary to-primary-dark p-6 text-white relative overflow-hidden">
                            {/* Decorative circles */}
                            <div className="absolute -top-10 -right-10 w-32 h-32 bg-white/10 rounded-full" />
                            <div className="absolute -bottom-5 -left-5 w-20 h-20 bg-white/5 rounded-full" />
                            
                            <DialogHeader className="relative z-10">
                                <div className="flex items-center gap-3 mb-2">
                                    <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                                        <CategoryIcon className="w-6 h-6 text-white" />
                                    </div>
                                    <div>
                                        <p className="text-white/70 text-sm font-medium">{t('loanForm.title')}</p>
                                        <DialogTitle className="text-2xl font-display font-bold text-white">
                                            {t('loanForm.loanFor')}: {t(`simulator.categories.${category}`)}
                                        </DialogTitle>
                                    </div>
                                </div>
                                <DialogDescription className="text-white/80 mt-2">
                                    {t('loanForm.description')}
                                </DialogDescription>
                            </DialogHeader>
                        </div>

                        {/* Form */}
                        <form onSubmit={handleSubmit} className="p-6 space-y-6">
                            {/* Personal Data Section */}
                            <div className="space-y-4">
                                <div className="flex items-center gap-2 text-primary font-semibold">
                                    <User className="w-5 h-5" />
                                    <span>{t('loanForm.personalData')}</span>
                                </div>
                                
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="name" className="text-sm font-medium">
                                            {t('loanForm.fullName')} *
                                        </Label>
                                        <div className="relative">
                                            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                            <Input
                                                id="name"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleInputChange}
                                                placeholder={t('loanForm.namePlaceholder')}
                                                required
                                                className="h-11 pl-10 rounded-xl"
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="phone" className="text-sm font-medium">
                                            {t('loanForm.phone')} *
                                        </Label>
                                        <div className="relative">
                                            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                            <Input
                                                id="phone"
                                                name="phone"
                                                type="tel"
                                                value={formData.phone}
                                                onChange={handleInputChange}
                                                placeholder={t('loanForm.phonePlaceholder')}
                                                required
                                                className="h-11 pl-10 rounded-xl"
                                            />
                                        </div>
                                    </div>
                                </div>
                                
                                <div className="space-y-2">
                                    <Label htmlFor="email" className="text-sm font-medium">
                                        {t('loanForm.email')} *
                                    </Label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                        <Input
                                            id="email"
                                            name="email"
                                            type="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            placeholder={t('loanForm.emailPlaceholder')}
                                            required
                                            className="h-11 pl-10 rounded-xl"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Financial Information Section */}
                            <div className="space-y-4">
                                <div className="flex items-center gap-2 text-accent font-semibold">
                                    <Euro className="w-5 h-5" />
                                    <span>{t('loanForm.financialInfo')}</span>
                                </div>
                                
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label htmlFor="currentDebt" className="text-sm font-medium">
                                            {t('loanForm.currentDebt')} *
                                        </Label>
                                        <div className="relative">
                                            <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                            <Input
                                                id="currentDebt"
                                                name="currentDebt"
                                                value={formData.currentDebt}
                                                onChange={handleAmountChange}
                                                placeholder="0"
                                                required
                                                className="h-11 pl-10 rounded-xl"
                                            />
                                        </div>
                                        <p className="text-xs text-muted-foreground">{t('loanForm.currentDebtHint')}</p>
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="requestedAmount" className="text-sm font-medium">
                                            {t('loanForm.requestedAmount')} *
                                        </Label>
                                        <div className="relative">
                                            <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                            <Input
                                                id="requestedAmount"
                                                name="requestedAmount"
                                                value={formData.requestedAmount}
                                                onChange={handleAmountChange}
                                                placeholder="0"
                                                required
                                                className="h-11 pl-10 rounded-xl"
                                            />
                                        </div>
                                        <p className="text-xs text-muted-foreground">{t('loanForm.requestedAmountHint')}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Terms Checkbox */}
                            <div className="flex items-start space-x-3 p-4 bg-muted/50 rounded-xl">
                                <Checkbox 
                                    id="acceptTerms" 
                                    checked={formData.acceptTerms}
                                    onCheckedChange={handleCheckboxChange}
                                    className="mt-0.5"
                                />
                                <label 
                                    htmlFor="acceptTerms" 
                                    className="text-sm text-muted-foreground leading-relaxed cursor-pointer"
                                >
                                    {t('loanForm.acceptTerms')}{' '}
                                    <a 
                                        href="https://www.spilkaconsulting.com/legal-notice" 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-primary hover:underline"
                                    >
                                        {t('footer.legalNotice')}
                                    </a>
                                    {' '}{t('loanForm.and')}{' '}
                                    <a 
                                        href="https://www.spilkaconsulting.com/privacy" 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="text-primary hover:underline"
                                    >
                                        {t('footer.privacy')}
                                    </a>
                                </label>
                            </div>

                            {/* Error Message */}
                            {error && (
                                <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-xl text-destructive text-sm">
                                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                    <span>{error}</span>
                                </div>
                            )}

                            {/* Submit Button */}
                            <Button 
                                type="submit" 
                                disabled={isSubmitting || !formData.acceptTerms}
                                className="w-full h-14 btn-gold rounded-xl text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
                            >
                                {isSubmitting ? (
                                    <>
                                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                        {t('loanForm.submitting')}
                                    </>
                                ) : (
                                    <>
                                        <CreditCard className="w-5 h-5 mr-2" />
                                        {t('loanForm.applyButton')}
                                    </>
                                )}
                            </Button>
                        </form>
                    </>
                ) : (
                    /* Success State */
                    <div className="p-8 text-center">
                        <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center mx-auto mb-6 animate-scale-in">
                            <CheckCircle2 className="w-12 h-12 text-primary" />
                        </div>
                        <h3 className="font-display text-2xl font-bold text-foreground mb-3">
                            {t('loanForm.successTitle')}
                        </h3>
                        <p className="text-muted-foreground mb-6 max-w-sm mx-auto">
                            {t('loanForm.successMessage')}
                        </p>
                        <Button 
                            onClick={handleClose}
                            className="btn-premium text-white rounded-xl px-8 h-12"
                        >
                            {t('contact.close')}
                        </Button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};
